import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Product } from '@mitang/shared';
import { RECENT_UPDATE_WINDOW_MS } from '@mitang/shared';
import { fetchCategoryCatalog, listenCategoryProducts } from '../lib/repo';
import { readCache, writeCache } from '../lib/localCache';
import { useAppConfig } from './useAppConfig';

export interface CategoryProductsResult {
  products: Product[];
  /** productIds observed to have changed recently (drives the flash). */
  recentlyUpdated: Set<string>;
  isLoading: boolean;
  error: Error | null;
  /** A fresh copy is being fetched right now (drives the "refreshing" chip). */
  refreshing: boolean;
  /** When the shown data was last confirmed fresh (epoch ms), if known. */
  freshAt: number | null;
}

interface CachedCatalog {
  version: number;
  freshAt: number;
  products: Product[];
}

/**
 * Products for ONE category — EDGE-FIRST with realtime notification.
 *
 *   1. Fetch the category catalog from the sync worker (one cached request; no
 *      per-product Firestore reads — the key cost/scale property).
 *   2. `app_config.catalogVersion` is a single realtime Firestore listener the
 *      app already runs; when the sync bumps it (seconds after a sheet edit),
 *      we re-fetch → updates stay effectively realtime.
 *   3. If the edge catalog is unreachable (not synced yet, offline, no token),
 *      fall back to the original per-product Firestore listener so the app
 *      always works.
 */
export function useCategoryProducts(category: string | null): CategoryProductsResult {
  const qc = useQueryClient();
  const key = ['products', category] as const;
  const config = useAppConfig();
  const version = config?.catalogVersion;

  const [error, setError] = useState<Error | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [freshAt, setFreshAt] = useState<number | null>(null);
  const seenRef = useRef<Map<string, number>>(new Map());
  const recentRef = useRef<Map<string, number>>(new Map());

  /** Note which rows changed since the last snapshot (drives the flash). */
  const noteChanges = (items: Product[]): void => {
    const now = Date.now();
    const seen = seenRef.current;
    for (const p of items) {
      const prev = seen.get(p.id);
      if (prev === undefined) {
        if (now - p.updatedAt < RECENT_UPDATE_WINDOW_MS) recentRef.current.set(p.id, p.updatedAt);
      } else if (p.updatedAt !== prev) {
        recentRef.current.set(p.id, now);
      }
      seen.set(p.id, p.updatedAt);
    }
  };

  // Reset per-category bookkeeping.
  useEffect(() => {
    seenRef.current = new Map();
    recentRef.current = new Map();
    setUsingFallback(false);
    setError(null);
  }, [category]);

  // 1 + 2. Device cache first (instant), then the edge catalog when the
  // realtime version says our copy is out of date.
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    const cacheKey = `catalog.${category}`;
    const cached = readCache<CachedCatalog>(cacheKey);

    // Paint immediately from the device — no network, no cost.
    if (cached?.products?.length) {
      qc.setQueryData<Product[]>(['products', category], cached.products);
      setFreshAt(cached.freshAt ?? null);
      setUsingFallback(false);
    }

    // Up to date already? Nothing to fetch.
    if (cached && version !== undefined && cached.version === version) return;

    setRefreshing(true);
    void fetchCategoryCatalog(category, version)
      .then((items) => {
        if (cancelled) return;
        if (items) {
          noteChanges(items);
          qc.setQueryData<Product[]>(['products', category], items);
          const now = Date.now();
          setFreshAt(now);
          setUsingFallback(false);
          writeCache(cacheKey, {
            version: version ?? now,
            freshAt: now,
            products: items,
          } satisfies CachedCatalog);
        } else if (!cached?.products?.length) {
          setUsingFallback(true); // → Firestore listener below
        }
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, version, qc]);

  // 3. Fallback: the original realtime per-product listener.
  useEffect(() => {
    if (!category || !usingFallback) return;
    const unsub = listenCategoryProducts(
      category,
      (items) => {
        noteChanges(items);
        setError(null);
        qc.setQueryData<Product[]>(['products', category], items);
      },
      (err) => setError(err),
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, usingFallback, qc]);

  const q = useQuery<Product[]>({
    queryKey: key,
    queryFn: () => qc.getQueryData<Product[]>(key) ?? [],
    enabled: !!category,
  });

  // Currently-recent set (prune expired entries).
  const now = Date.now();
  const recent = new Set<string>();
  for (const [id, ts] of recentRef.current) {
    if (now - ts < RECENT_UPDATE_WINDOW_MS) recent.add(id);
    else recentRef.current.delete(id);
  }

  return {
    products: q.data ?? [],
    recentlyUpdated: recent,
    isLoading: !!category && q.data === undefined && error === null,
    error,
    refreshing,
    freshAt,
  };
}
